import React, { useState, useEffect } from 'react'
import {
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChartBarIcon,
  TruckIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  FireIcon,
  SparklesIcon,
  ShoppingBagIcon,
  UserIcon,

  HeartIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline'
import { Modal } from '../../../components/Modal'
import { Button } from '../../../components/Button'
import { InternalMessageModal } from './InternalMessageModal'
import { useStaff } from '../../../contexts/StaffContext'

// Props cho nút hành động
interface ActionButtonProps {
  icon: React.FC<{ className?: string }> // Icon component
  label: string // Nhãn hiển thị
  description?: string // Mô tả chi tiết (optional)
  onClick: () => void // Hàm xử lý khi click
  selected?: boolean // Trạng thái được chọn
  onSelect?: () => void // Callback khi được chọn
}

/**
 * Component hiển thị skeleton cho nút hành động
 * - Hiển thị placeholder cho icon, tiêu đề và mô tả
 * - Sử dụng animate-pulse để tạo hiệu ứng loading
 */
const ActionButtonSkeleton = () => (
  <div className="relative flex flex-col items-center justify-between px-4 py-3 rounded-lg w-full shadow-lg shadow-gray-200 bg-white border-2 border-gray-100">
    <div className="flex flex-col items-center text-center space-y-2 w-full">
      <div className="w-10 h-10 rounded-full bg-gray-100 animate-pulse" /> {/* Placeholder cho icon */}
      <div className="w-full">
        <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4 mx-auto" /> {/* Placeholder cho tiêu đề */}
        <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2 mx-auto mt-2" /> {/* Placeholder cho mô tả */}
      </div>
    </div>
  </div>
)

/**
 * Component hiển thị nút hành động có thể chọn/bỏ chọn
 * - Hiển thị icon, label và mô tả
 * - Thay đổi style khi được chọn
 * - Xử lý sự kiện click
 */
const ActionButton: React.FC<ActionButtonProps> = ({ 
  icon: Icon, 
  label, 
  description,
  selected,
  onSelect
}) => (
  <button
    onClick={onSelect}
    className={`relative flex flex-col items-center justify-between px-4 py-3 rounded-lg transition-all w-full shadow-lg shadow-gray-200
    ${selected 
      ? 'bg-white border-2 border-blue-600' // Style khi được chọn
      : 'bg-white border-2 border-gray-100'}`} // Style mặc định
  >
    <div className="flex flex-col items-center text-center space-y-2 w-full">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center
        ${selected ? 'bg-blue-100' : 'bg-blue-100/50'}`}>
        <Icon className={`w-5 h-5 ${selected ? 'text-blue-600' : 'text-blue-500'}`} />
      </div>
      <div className="w-full">
        <div className={`font-medium truncate ${selected ? 'text-blue-700' : 'text-blue-600'}`} 
          title={label}>{label}</div>
        {description && (
          <div className="text-sm text-gray-500 line-clamp-2" 
            title={description}>{description}</div>
        )}
      </div>
      {selected && (
        <div className="absolute top-0 right-2">
          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
    </div>
  </button>
)

/**
 * Component hiển thị hiệu ứng đang suy nghĩ và tin nhắn
 * - Hiển thị skeleton lines khi đang suy nghĩ
 * - Hiển thị tin nhắn sau khi hết thời gian suy nghĩ
 * - Gọi callback khi hoàn thành
 */
const ThinkingMessage: React.FC<{ 
  text: string; // Nội dung tin nhắn
  thinkingTime?: number; // Thời gian hiển thị hiệu ứng (ms)
  onComplete?: () => void; // Callback khi hoàn thành
}> = ({ text, thinkingTime = 2000, onComplete }) => {
  const [isThinking, setIsThinking] = useState(true)
  const [skeletonLines, setSkeletonLines] = useState<number[]>([])

  useEffect(() => {
    const numLines = Math.floor(Math.random() * 2) + 2
    const lines = []
    lines.push(Math.floor(Math.random() * 11) + 85)
    lines.push(Math.floor(Math.random() * 16) + 60)
    if (numLines === 3) {
      lines.push(Math.floor(Math.random() * 16) + 40)
    }
    setSkeletonLines(lines)

    const timer = setTimeout(() => {
      setIsThinking(false)
      onComplete && onComplete()
    }, thinkingTime)
    return () => clearTimeout(timer)
  }, [thinkingTime, onComplete])

  if (isThinking) {
    return (
      <div className="space-y-2">
        {skeletonLines.map((width, index) => (
          <div 
            key={index}
            className={`h-3 bg-current opacity-10 rounded-full animate-pulse`}
            style={{ width: `${width}%` }}
          />
        ))}
      </div>
    )
  }

  return (
    <span className="animate-fade-in">
      {text}
    </span>
  )
}

// Component icon AI với hiệu ứng động
const AIStarsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <div className="relative">
    {/* Hiệu ứng pulse cho background */}
    <div className="absolute inset-0 rounded-full animate-pulse opacity-50" />
    
    {/* Icon chính */}
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      strokeWidth="1.5" 
      stroke="currentColor" 
      className={`${className} relative z-10 animate-bounce`}
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
      />
    </svg>
  </div>
)

// Thêm styles cho animation fade-in
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
  }
`

// Thêm styles vào head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style")
  styleSheet.innerText = styles
  document.head.appendChild(styleSheet)
}

/**
 * Component khối gợi ý với icon và màu sắc tùy chỉnh
 * - Hiển thị tin nhắn với hiệu ứng suy nghĩ
 * - Cho phép tùy chỉnh icon và màu sắc
 * - Gọi callback khi hoàn thành hiển thị
 */
const SuggestionBlock: React.FC<{
  text: string; // Nội dung gợi ý
  icon: React.FC<{ className?: string }>; // Icon component
  bgColor: string; // Màu nền
  iconBgColor: string; // Màu nền icon
  textColor: string; // Màu chữ
  onComplete?: () => void; // Callback khi hoàn thành
}> = ({ text, icon: Icon, bgColor, iconBgColor, textColor, onComplete }) => {
  return (
    <div className={`${bgColor} rounded-lg p-4`}>
      <div className="flex items-start space-x-3">
        <div className={`w-10 h-10 ${iconBgColor} rounded-full flex-shrink-0 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${textColor}`} />
        </div>
        <div className="space-y-1 flex-1">
          <div className={`${textColor}`}>
            <ThinkingMessage text={text} onComplete={onComplete} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface ModalActionProps {
  onClose: () => void;
  onActionSelect: (actionType: string) => void;
  selectedAction: string | null;
}

interface AIMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: string;
  message: any;
}

interface ModalContentProps {
  message: any;
  modalActions: ModalActionProps;
  isOpen?: boolean;
}

// Modal cảnh báo về khách hàng (rời bỏ hoặc đơn hàng trễ)
export const CustomerAlertModal: React.FC<ModalContentProps> = ({ message, modalActions, isOpen = false }) => {
  const { staffInfo } = useStaff()
  const [messageContent, setMessageContent] = useState('')
  const [charCount, setCharCount] = useState(0)
  const [showActions, setShowActions] = useState(false)
  
  const isCustomerLeaving = message.title.includes('rời bỏ')
  const alertText = isCustomerLeaving
    ? `${staffInfo.nickname} ơi, khách hàng không có tương tác mua hàng trong 3 tháng, bạn cần tìm hiểu nguyên nhân và gửi ưu đãi để giữ chân khách nhé.`
    : `${staffInfo.fullName} ơi, đơn hàng #DH123 có thể sẽ giao đến khách trễ 1 ngày, bạn liên hệ khách ngay để cập nhật tình trạng và xin lỗi về sự chậm trễ hen!`

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value
    if (content.length <= 200) {
      setMessageContent(content)
      setCharCount(content.length)
    }
  }

  const customerLeavingActions: ActionButtonProps[] = [
    {
      icon: ChatBubbleLeftIcon,
      label: 'Gửi tin nhắn',
      description: 'Gửi khảo sát và ưu đãi giữ chân',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'message',
      onSelect: () => modalActions?.onActionSelect?.('message')
    },
    {
      icon: FireIcon,
      label: 'Gửi ưu đãi',
      description: 'Voucher giảm 25% cho đơn hàng tiếp theo',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'offer',
      onSelect: () => modalActions?.onActionSelect?.('offer')
    }
  ]

  const lateDeliveryActions: ActionButtonProps[] = [
    {
      icon: ChatBubbleLeftIcon,
      label: 'Gửi tin nhắn',
      description: 'Thông báo lịch giao mới và gửi lời xin lỗi',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'message',
      onSelect: () => modalActions?.onActionSelect?.('message')
    },
    {
      icon: TruckIcon,
      label: 'Liên hệ giao hàng',
      description: 'Liên hệ bộ phận giao hàng để ưu tiên',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'delivery',
      onSelect: () => modalActions?.onActionSelect?.('delivery')
    }
  ]

  const actionButtons = isCustomerLeaving ? customerLeavingActions : lateDeliveryActions

  // Footer - popup cảnh báo khách hàng và cảnh báo đơn hàng
  const footer = (
    <div className="flex space-x-2">
      <button 
        className="flex-1 px-4 py-2.5 text-gray-600 hover:text-gray-700 border border-none rounded-lg hover:bg-gray-50 transition-colors font-medium"
        onClick={modalActions.onClose}
      >
        Để sau
      </button>
      <button 
        className={`flex-1 px-4 py-2.5 rounded-lg transition-colors font-medium ${
          modalActions.selectedAction 
            ? 'bg-blue-600 text-white hover:bg-blue-700' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        onClick={() => {
          if (modalActions.selectedAction) {
            modalActions.onClose()
          }
        }}
        disabled={!modalActions.selectedAction}
      >
        Thực hiện ngay
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={message.title}
      icon={<ExclamationTriangleIcon className="w-6 h-6 text-red-500" />}
      iconBg="bg-red-50"
      footer={footer}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <SuggestionBlock 
            text={alertText}
            icon={AIStarsIcon}
            bgColor="bg-red-50"
            iconBgColor="bg-red-100"
            textColor="text-red-600"
            onComplete={() => setShowActions(true)}
          />

          {/* Các hành động cần thực hiện */}
          {showActions ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-3 pl-2">
                <h4 className="font-medium">Chọn một gợi ý để thực hiện!</h4>
              </div>
              
              {/* Grid các action buttons */}
              <div className="grid grid-cols-2 gap-3">
                {actionButtons.map((action, index) => (
                  <ActionButton key={index} {...action} />
                ))}
              </div>

              {/* Ô nhập nội dung tin nhắn */}
              <div>
                <label className="text-sm text-gray-600 block pl-2">
                  Lời nhắn kèm theo
                </label>
                <div className="relative p-0.5">
                  <textarea
                    className="w-full min-h-[60px] max-h-[60px] px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder={isCustomerLeaving 
                      ? "Nhập nội dung khảo sát và ưu đãi..." 
                      : "Nhập nội dung thông báo và xin lỗi..."}
                    rows={3}
                    value={messageContent}
                    onChange={handleMessageChange}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400 mb-1">
                    {charCount}/200
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-3 pl-2">
                <h4 className="font-medium">Chọn một gợi ý để thực hiện!</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ActionButtonSkeleton />
                <ActionButtonSkeleton />
              </div>
            </div>
          )}
        </div>

        {/* Phần thông tin chi tiết */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <h4 className="font-medium text-gray-900 pl-2">
              {isCustomerLeaving ? 'Thông tin khách hàng' : 'Thông tin đơn hàng'}
            </h4>
          </div>
          
          <div className="bg-gray-50 rounded-lg space-y-2 p-4">
            {isCustomerLeaving ? (
              <>
                {/* Thông tin khách hàng */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-base text-gray-900">Nguyễn Văn A</span>
                    <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-600 rounded text-xs">
                      Hạng vàng
                    </span>
                  </div>
                  <div className="flex items-center text-sm font-medium space-x-1 text-blue-600">
                    <span>12.000.000đ</span>
                  </div>
                </div>

                {/* Số đơn đã mua */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <ShoppingBagIcon className="w-4 h-4" />
                  <span>Đã mua: <span className="font-medium">3</span> đơn</span>
                </div>

                {/* Sản phẩm yêu thích */}
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <HeartIcon className="w-4 h-4 mt-0.5" />
                  <div className="flex items-center space-x-2">
                    <span>Sản phẩm </span>
                    <div className="flex flex-wrap gap-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Túi xách</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Giày cao cấp</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Đồng hồ</span>
                    </div>
                  </div>
                </div>

                {/* Lần mua gần nhất */}
                <div className="flex items-center space-x-2 text-sm text-red-500">
                  <CalendarIcon className="w-4 h-4" />
                  <span>Lần mua gần nhất cách đây 3 tháng</span>
                </div>
              </>
            ) : (
              <>
                {/* Thông tin đơn hàng */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-base text-gray-900">#DH123</span>
                    <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs">
                      Giao hàng trễ
                    </span>
                  </div>
                  <div className="flex items-center text-sm font-medium space-x-1 text-blue-600">
                    <span>2.500.000đ</span>
                  </div>
                </div>

                {/* Thông tin khách hàng */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <UserIcon className="w-4 h-4" />
                  <div className="flex items-center space-x-2">
                    <span>Khách </span>
                    <span className="font-medium">Nguyễn Thị B</span>
                  </div>
                </div>

                {/* Sản phẩm trong đơn */}
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <ShoppingBagIcon className="w-4 h-4 mt-0.5" />
                  <div className="flex items-center space-x-2">
                    <span>Sản phẩm </span>
                    <div className="flex flex-wrap gap-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Túi xách LV x1</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Ví cầm tay x1</span>
                    </div>
                  </div>
                </div>

                {/* Địa chỉ giao hàng */}
                <div className="flex items-start space-x-2 text-sm text-gray-600">
                  <MapPinIcon className="w-4 h-4 mt-0.5" />
                  <span>203 NKKN, Võ Thị Sáu, Q3, TP.HCM</span>
                </div>

                {/* Thời gian đặt hàng */}
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CalendarIcon className="w-4 h-4" />
                  <span>Đặt ngày 15/02</span>
                  <span className="text-red-500">Dự kiến giao: 17/02</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// Modal hiển thị cơ hội bán hàng
export const SalesOpportunityModal: React.FC<ModalContentProps> = ({ message, modalActions, isOpen = false }) => {
  const { staffInfo } = useStaff()
  const [messageContent, setMessageContent] = useState('')
  const [charCount, setCharCount] = useState(0)
  const [showActions, setShowActions] = useState(false)
  const [showCustomers, setShowCustomers] = useState(false)

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value
    if (content.length <= 200) {
      setMessageContent(content)
      setCharCount(content.length)
    }
  }

  const opportunityText = `${staffInfo.fullName} ơi, có cơ hội tốt tăng doanh số cho ${staffInfo.store} nè! bạn gửi gói combo với ưu đãi 25% đến khách hàng đang quan tâm sản phẩm cao cấp hen!`

  const CUSTOMER_GROUPS = {
    offer: {
      count: 3,
      customers: ['A', 'B', 'C'],
      products: 'Túi xách LV, Giày cao góc',
      bgColor: 'bg-emerald-50',
      avatarBgColor: 'bg-emerald-100',
      textColor: 'text-emerald-600'
    },
    promotion: {
      count: 5,
      customers: ['G', 'H', 'I', 'M', 'S'],
      products: 'Túi xách, phụ kiện cao cấp',
      bgColor: 'bg-blue-50',
      avatarBgColor: 'bg-blue-100',
      textColor: 'text-blue-600'
    }
  }

  const actionButtons: ActionButtonProps[] = [
    {
      icon: FireIcon,
      label: 'Gửi ưu đãi 25%',
      description: 'Gửi voucher combo ưu đãi đặc biệt',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'offer',
      onSelect: () => {
        modalActions?.onActionSelect?.('offer')
        setShowCustomers(true)
      }
    },
    {
      icon: SparklesIcon,
      label: 'Gửi khuyến mãi',
      description: 'Khuyến mãi cho sản phẩm cao cấp',
      onClick: () => {},
      selected: modalActions?.selectedAction === 'promotion',
      onSelect: () => {
        modalActions?.onActionSelect?.('promotion')
        setShowCustomers(true)
      }
    }
  ]

  // Footer - popup cơ hội bán hàng
  const footer = (
    <div className="flex space-x-2">
      <button 
        className="flex-1 px-4 py-2.5 text-gray-600 hover:text-gray-700 border border-none rounded-lg hover:bg-gray-50 transition-colors font-medium"
        onClick={modalActions.onClose}
      >
        Để sau
      </button>
      <button 
        className={`flex-1 px-4 py-2.5 rounded-lg transition-colors font-medium ${
          modalActions.selectedAction 
            ? 'bg-blue-600 text-white hover:bg-blue-700' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        onClick={() => {
          if (modalActions.selectedAction) {
            modalActions.onClose()
          }
        }}
        disabled={!modalActions.selectedAction}
      >
        Thực hiện ngay
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={message.title}
      icon={<ArrowTrendingUpIcon className="w-6 h-6 text-emerald-500" />}
      iconBg="bg-emerald-50"
      footer={footer}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <SuggestionBlock 
            text={opportunityText}
            icon={AIStarsIcon}
            bgColor="bg-emerald-50"
            iconBgColor="bg-emerald-100"
            textColor="text-emerald-600"
            onComplete={() => {
              setShowActions(true)
            }}
          />

          {/* Các hành động cần thực hiện */}
          {showActions ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-3">
                <h4 className="font-medium">Chọn một gợi ý để thực hiện!</h4>
              </div>
              
              {/* Grid các action buttons */}
              <div className="grid grid-cols-2 gap-3">
                {actionButtons.map((action: ActionButtonProps, index: number) => (
                  <ActionButton key={index} {...action} />
                ))}
              </div>

              {/* Ô nhập nội dung tin nhắn */}
              <div>
                <label className="text-sm text-gray-600 mb-2 block">
                  Lời nhắn kèm theo
                </label>
                <div className="relative p-0.5">
                  <textarea
                    className="w-full min-h-[60px] max-h-[60px] px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="Nhập nội dung ưu đãi và tư vấn..."
                    rows={3}
                    value={messageContent}
                    onChange={handleMessageChange}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                    {charCount}/200
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-3">
                <h4 className="font-medium">Chọn một gợi ý để thực hiện!</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ActionButtonSkeleton />
                <ActionButtonSkeleton />
              </div>
            </div>
          )}

          {/* Danh sách khách hàng */}
          {modalActions.selectedAction && showCustomers && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Nhóm khách hàng tiềm năng</h4>
              <div className="space-y-4">
                <div className={`${CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].bgColor} rounded-lg p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900">
                      Có {CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].count} khách có khả năng mua
                    </h5>
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].customers.map((letter) => (
                        <div key={letter} className={`w-8 h-8 rounded-full ${CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].avatarBgColor} flex items-center justify-center`}>
                          <span className={`text-sm font-medium ${CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].textColor}`}>{letter}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <ShoppingBagIcon className="w-4 h-4" />
                      <span>Sản phẩm: {CUSTOMER_GROUPS[modalActions.selectedAction as keyof typeof CUSTOMER_GROUPS].products}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

// Modal nhắc nhở về chính sách và khuyến mãi
export const PolicyReminderModal: React.FC<ModalContentProps> = ({ message, modalActions, isOpen = false }) => {
  const { staffInfo } = useStaff()
  const [selectedGroups, setSelectedGroups] = useState<string[]>(['pending'])
  const [showGroups, setShowGroups] = useState(false)
  
  const policyText = `${staffInfo.nickname} ơi, đang có chương trình giảm 25% + voucher 500k đến 30/4. ${staffInfo.nickname} ưu tiên chia sẻ cho 3 khách VIP và 2 khách đang có đơn chờ để tăng tỷ lệ chốt đơn của ${staffInfo.store} hen!`

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId)
      }
      return [...prev, groupId]
    })
  }

  const getGroupStyles = (groupId: string) => {
    const isSelected = selectedGroups.includes(groupId)
    if (groupId === 'pending') {
      return {
        container: `${isSelected ? 'bg-emerald-50 border-emerald-500' : 'bg-gray-50 border-transparent'} border-2`,
        title: isSelected ? 'text-emerald-700' : 'text-gray-900',
        check: 'text-emerald-500'
      }
    }
    return {
      container: `${isSelected ? 'bg-blue-50 border-blue-500' : 'bg-gray-50 border-transparent'} border-2`,
      title: isSelected ? 'text-blue-700' : 'text-gray-900',
      check: 'text-blue-500'
    }
  }

  const handleSendNotification = () => {
    modalActions.onActionSelect(selectedGroups.join(','))
    modalActions.onClose()
  }

  // Footer - popup nhắc nhở chính sách và khuyến mãi
  const footer = (
    <div className="flex justify-end space-x-2">
      <Button
        className="flex-1 px-4 py-2.5 text-gray-600 hover:text-gray-700 border border-none rounded-lg hover:bg-gray-50 transition-colors font-medium"
        variant="outline"
        onClick={modalActions.onClose}
        disabled={!showGroups}
      >
        Để sau
      </Button>
      <Button
        className="flex-1 px-4 py-2.5 rounded-lg transition-colors font-medium" 
        variant="primary"
        onClick={handleSendNotification}
        disabled={!showGroups || selectedGroups.length === 0}
      >
        Thực hiện ngay
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={message.title}
      icon={<BellAlertIcon className="w-6 h-6 text-blue-500" />}
      iconBg="bg-blue-50"
      footer={footer}
    >
      <div className="space-y-6">
        <SuggestionBlock 
          text={policyText}
          icon={AIStarsIcon}
          bgColor="bg-blue-50"
          iconBgColor="bg-blue-100"
          textColor="text-blue-600"
          onComplete={() => { 
            setShowGroups(true)
           }}
        />

        {/* Khách hàng cần thông báo */}
        <div className="space-y-3">
          {showGroups ? (
            <div className="space-y-4">
              {/* Khách có đơn chờ */}
              <h4 className="font-medium text-gray-900">Nhóm khách nên gửi</h4>
              <div 
                className={`rounded-lg p-4 cursor-pointer transition-all ${getGroupStyles('pending').container}`}
                onClick={() => handleGroupSelect('pending')}
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className={`font-medium ${getGroupStyles('pending').title}`}>
                    Nhóm đang có đơn chờ 3 khách
                  </h5>
                  {selectedGroups.includes('pending') && (
                    <CheckIcon className={`w-5 h-5 ${getGroupStyles('pending').check}`} />
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {['P', 'Q', 'S'].map(letter => (
                      <div key={letter} className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-emerald-600">{letter}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <ShoppingBagIcon className="w-4 h-4" />
                    <span>Sản phẩm: Áo khoác, phụ kiện</span>
                  </div>
                </div>
              </div>

              {/* Nhóm tiềm năng */}
              <div 
                className={`rounded-lg p-4 cursor-pointer transition-all ${getGroupStyles('potential').container}`}
                onClick={() => handleGroupSelect('potential')}
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className={`font-medium ${getGroupStyles('potential').title}`}>
                    Nhóm tiềm năng 7 khách
                  </h5>
                  {selectedGroups.includes('potential') && (
                    <CheckIcon className={`w-5 h-5 ${getGroupStyles('potential').check}`} />
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(letter => (
                      <div key={letter} className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">{letter}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <ShoppingBagIcon className="w-4 h-4" />
                    <span>Sản phẩm: Túi xách, giày cao cấp</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

// Modal hiển thị thông tin lịch hẹn với khách hàng
export const AppointmentModal: React.FC<ModalContentProps> = ({ message, modalActions, isOpen = false }) => {
  const { staffInfo } = useStaff()
  const [showNote, setShowNote] = useState(false)
  
  const appointmentText = `${staffInfo.fullName} ơi, khách có hẹn 15h hôm nay tại ${staffInfo.store}. Bạn chuẩn bị catalogue mới và demo sản phẩm cao cấp cho khách này nhé, họ đang có nhu cầu mua combo!`

  const footer = (
    <div className="flex justify-center w-full">
      <Button
        variant="primary"
        onClick={modalActions.onClose}
        className="w-full"
        disabled={!showNote}
      >
        OK, tôi nhớ rồi hen!
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={message.title}
      icon={<ClockIcon className="w-6 h-6 text-blue-500" />}
      iconBg="bg-blue-50"
      footer={footer}
    >
      <div className="space-y-6">
        <SuggestionBlock 
          text={appointmentText}
          icon={AIStarsIcon}
          bgColor="bg-yellow-50"
          iconBgColor="bg-yellow-100"
          textColor="text-yellow-700"
          onComplete={() => {
            setTimeout(() => setShowNote(true), 300)
          }}
        />

        {/* Chi tiết */}
        <div className="space-y-3">
          <h4 className="font-medium">Thông tin lịch hẹn</h4>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            {/* Thời gian */}
            <div className="flex items-center space-x-2 text-gray-700">
              <ClockIcon className="w-5 h-5 text-blue-600" />
              <div>
                <span className="font-medium">15 - 16h hôm nay</span>
                <span className="text-sm text-blue-600 ml-2">(Còn 2 giờ nữa)</span>
              </div>
            </div>

            {/* Thông tin khách */}
            <div className="flex items-center space-x-2 text-gray-700">
              <UserIcon className="w-5 h-5 text-blue-600" />
              <div className="flex items-center space-x-2">
                <span className="font-medium">Lê Văn C</span>
                <span className="px-2 py-0.5 bg-yellow-50 text-yellow-600 rounded text-xs">VIP</span>
              </div>
            </div>

            {/* Lịch sử mua hàng */}
            <div className="flex items-start space-x-2 text-gray-700">
              <CurrencyDollarIcon className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="space-y-1">
                <div className="font-medium">Đã chi tiêu 45tr trong 3 tháng gần đây</div>
                <div className="flex gap-1 text-center">
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs">Túi xách cao cấp (25tr)</span>
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs">Giày cao gót (12tr)</span>
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs">Phụ kiện (8tr)</span>
                </div>
              </div>
            </div>

            {/* Địa điểm */}
            <div className="flex items-start space-x-2 text-gray-700">
              <MapPinIcon className="w-5 h-5 text-blue-600" />
              <div className="space-y-1">
                <div className="font-medium">Cửa hàng Flagship Quận 1</div>
                <div className="text-sm">123 Đồng Khởi, Bến Nghé, Q1, TP.HCM</div>
              </div>
            </div>

            {/* Ghi chú */}
            {showNote && (
              <div className="flex items-start space-x-2 text-gray-700 bg-green-100 p-2 rounded animate-fade-in">
                <SparklesIcon className="w-8 h-8 text-green-600" />
                <div className="text-sm text-green-700">
                  Khách đang quan tâm bộ sưu tập mới Xuân-Hè, có thể chốt combo 50-100tr
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// Modal hiển thị báo cáo doanh số
export const SalesReportModal: React.FC<ModalContentProps> = ({ message, modalActions, isOpen = false }) => {
  const { staffInfo } = useStaff()
  const [showDailyStats, setShowDailyStats] = useState(false)
  const [showCustomerStats, setShowCustomerStats] = useState(false)
  const [showProductStats, setShowProductStats] = useState(false)

  const reportText = `${staffInfo.nickname} ơi, doanh số ${staffInfo.store} đạt được 80% chỉ tiêu (40/50tr) rùi hen. Bạn tập trung bán thêm túi xách và giày cao cấp đang hot để đạt 100% chỉ tiêu tháng này nhé!`

  useEffect(() => {
    const timer1 = setTimeout(() => setShowDailyStats(true), 500)
    const timer2 = setTimeout(() => setShowCustomerStats(true), 1000)
    const timer3 = setTimeout(() => setShowProductStats(true), 1500)
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [])

  const footer = (
    <div className="flex justify-center w-full">
      <Button
        variant="primary"
        onClick={modalActions.onClose}
        className="w-full"
        disabled={!showDailyStats || !showCustomerStats || !showProductStats}
      >
        Yeah! Tôi đã hiểu
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={message.title}
      icon={<ChartBarIcon className="w-6 h-6 text-blue-500" />}
      iconBg="bg-blue-50"
      footer={footer}
    >
      <div className="space-y-6">
        <SuggestionBlock 
          text={reportText}
          icon={AIStarsIcon}
          bgColor="bg-blue-50"
          iconBgColor="bg-blue-100"
          textColor="text-blue-600"
        />

        <div className="space-y-6">
          {/* Biểu đồ theo ngày */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium mb-3">Theo ngày trong tuần</h4>
            {!showDailyStats ? (
              <div className="animate-pulse space-y-3">
                <div className="h-24 bg-gray-200 rounded-lg"></div>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {[
                  { day: 'CN', value: 4.0 },
                  { day: 'T2', value: 6.0 },
                  { day: 'T3', value: 7.5 },
                  { day: 'T4', value: 5.0 },
                  { day: 'T5', value: 9.0 },
                  { day: 'T6', value: 12.5 },
                  { day: 'T7', value: 10.0 }
                ].map(({ day, value }) => (
                  <div key={day} className="text-center">
                    <div className="text-sm text-gray-600 mb-2">{day}</div>
                    <div className="h-16 bg-gray-200 rounded-lg relative">
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-green-500 rounded-lg transition-all"
                        style={{ height: `${(value/12.5)*100}%`, opacity: 0.3 + (value/12.5)*0.7 }}
                      />
                      <div className="absolute bottom-2 left-0 right-0 text-xs font-medium">{value.toFixed(1)}M</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Theo nhóm khách hàng */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium mb-3">Theo nhóm khách hàng</h4>
            {!showCustomerStats ? (
              <div className="animate-pulse space-y-3">
                <div className="h-24 bg-gray-200 rounded-lg"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  { name: 'VIP', value: 18, percent: 45 },
                  { name: 'Thân thiết', value: 13, percent: 32 },
                  { name: 'Mới', value: 9, percent: 23 }
                ].map(({ name, value, percent }) => (
                  <div key={name} className="flex items-center">
                    <div className="w-24 text-sm">{name}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                    <div className="w-32 text-right text-sm">
                      <span className="font-medium">{value.toFixed(1)}M</span>
                      <span className="text-gray-500 ml-1">({percent}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top sản phẩm */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium mb-3">Top sản phẩm bán chạy</h4>
            {!showProductStats ? (
              <div className="animate-pulse space-y-3">
                <div className="h-24 bg-gray-200 rounded-lg"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  { name: 'Túi xách cao cấp', value: 12.5, quantity: 5, percent: 100 },
                  { name: 'Giày thể thao', value: 9.0, quantity: 4, percent: 72 },
                  { name: 'Áo khoác', value: 7.5, quantity: 3, percent: 60 }
                ].map(({ name, value, quantity, percent }) => (
                  <div key={name} className="flex items-center">
                    <div className="w-24 text-sm truncate">{name}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full rounded-full bg-green-500" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                    <div className="w-32 text-right text-sm">
                      <span className="font-medium">{value.toFixed(1)}M</span>
                      <span className="text-gray-500 ml-1">({quantity} sp)</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// Component modal chính quản lý hiển thị các loại modal khác nhau
export const AIMessageModal: React.FC<AIMessageModalProps> = ({ isOpen, onClose, type, message }) => {
  // State quản lý hành động được chọn
  const [selectedAction, setSelectedAction] = useState<string | null>(null)

  // Props truyền xuống cho các modal con
  const modalActions: ModalActionProps = {
    onClose: () => {
      setSelectedAction(null)
      onClose()
    },
    onActionSelect: (actionType: string) => {
      setSelectedAction(actionType)
    },
    selectedAction
  }

  // Render modal tương ứng theo type
  const renderModalContent = () => {
    switch (type) {
      case 'customer_alert':
        return <CustomerAlertModal message={message} modalActions={modalActions} />
      case 'sales_opportunity':
        return <SalesOpportunityModal message={message} modalActions={modalActions} />
      case 'policy_reminder':
        return <PolicyReminderModal message={message} modalActions={modalActions} />
      case 'appointment':
        return <AppointmentModal message={message} modalActions={modalActions} />
      case 'sales_report':
        return <SalesReportModal message={message} modalActions={modalActions} />
      case 'internal':
        return <InternalMessageModal message={message} modalActions={modalActions} />
      default:
        return null
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={message?.title || 'Thông báo'}
    >
      <div className="">
          {renderModalContent()}
        </div>
    </Modal>
  )
}

/**
 * Component hiển thị skeleton cho nhóm khách hàng
 * - Hiển thị placeholder cho tiêu đề nhóm
 * - Hiển thị các avatar placeholder với số lượng ngẫu nhiên
 * - Hiển thị placeholder cho thông tin sản phẩm với độ rộng ngẫu nhiên
 */
const CustomerGroupSkeleton = () => {
  // Random số lượng avatar (2-5)
  const avatarCount = Math.floor(Math.random() * 4) + 2
  
  // Random độ rộng cho các placeholder
  const titleWidth = Math.floor(Math.random() * 20) + 30 // 30-50%
  const textWidth = Math.floor(Math.random() * 20) + 50 // 50-70%
  
  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${titleWidth}%` }} />
      </div>
      <div className="space-y-3">
        {/* Placeholder cho avatar khách hàng */}
        <div className="flex flex-wrap items-center gap-2">
          {Array(avatarCount).fill(0).map((_, i) => (
            <div key={i} className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
          ))}
        </div>
        {/* Placeholder cho thông tin sản phẩm */}
        <div className="flex items-center space-x-2">
          <div className="w-10 h-3 bg-gray-200 rounded animate-pulse" />
          <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${textWidth}%` }} />
        </div>
      </div>
    </div>
  )
} 